/**
 * product-create/form — 제품 등록 한 폼의 순수 함수(VOCABULARY "제품 등록 — 한 폼", 2026-10-22).
 * 폼은 글만 쥔다(`ProductFormData`). 담기 · 칸 고침 · 저장 막기 · 보낼 입력 만들기 — 규칙은 여기 한 곳, 뷰는 액션과 그리기만.
 */
import { toSpecCategory } from '@hcltech/shared/server-config/spec-category';
import type { SpecCategory } from '@hcltech/shared/server-config/spec-category';
import type { CatalogPick, EDescTokenRole, PartProductInput, ServerProductInput, SlotFill } from '../../api/client';
import { assert } from '../../lib/assert';
import { missingSlotRolesOf, trimmedFillsOf, withSlotFill } from '../../components/slot-reading';
import type { EFindFillField, EProductFormField, FormLookup, ProductFormData } from './types';

const CHASSIS_CATEGORY: SpecCategory = 'CHASSIS';
/** 이 셋이 바뀌면 담아 온 행 · 찾기 결과가 더는 맞지 않는다 — 비운다 */
const LOOKUP_KEY_FIELDS: readonly EProductFormField[] = ['spec_category', 'part_number'];
/** 제조사 셀렉트의 "기타 — 직접 적기" 값 — 고르면 글 칸이 열린다(제품 상세 `VENDOR_OTHER` 와 같은 값) */
export const VENDOR_OTHER = '__other__';

/** 빈 폼 — 찾기 글 '' · 카테고리 '' · 글 칸 '' · 제조사 후보 [] · picked · lookup null · saving false · error '' */
export function emptyProductForm(): ProductFormData {
  return { missed_query: null, finding: false, catalog_query: '', spec_category: '', part_number: '', vendor: '', vendor_other: false, vendors: [], description: '', description_rule_id: null, rules: [], reading: null, slot_fills: [], search_text: '', notes: '', picked: null, lookup: null, saving: false, error: '' };
}

/**
 * 카탈로그에서 담아 온 행을 폼에 붓는다.
 * 약속: 카테고리 = `toSpecCategory(pick.table)`(chassis → CHASSIS. null 이면 assert — 표 17 은 전부 21종에 든다) ·
 * 파트넘버 = `option_part` · 제조사 = `vendor`(null 이면 비움) · 디스크립션 = `description`(null 이면 비움 — 사람이 친다) · `picked` = pick · `lookup` = null(뷰가 다시 받는다).
 * 검색어 · 메모는 그대로. 이어지면 제조사 칸은 잠긴다(카탈로그 것).
 */
export function formWithPick(form: ProductFormData, pick: CatalogPick): ProductFormData {
  const category = toSpecCategory(pick.table);
  assert(category !== null, `카탈로그 표 17 은 전부 21종에 든다 — 모르는 표가 담겨 왔다: ${pick.table}`);
  return {
    ...form,
    spec_category: category,
    part_number: pick.option_part,
    vendor: pick.vendor === null ? '' : pick.vendor,
    vendor_other: false,
    description: pick.description === null ? '' : pick.description,
    picked: pick,
    lookup: null,
    error: '',
  };
}

/**
 * 칸 하나를 사람이 고친 폼.
 * 약속: 카테고리 · 파트넘버가 바뀌면 `picked` · `lookup` 을 비운다(담아 온 행과 어긋남 — 칩이 사라지고 찾기를 다시) · 카탈로그가 넣은 제조사도 비운다. 디스크립션 · 검색어 · 메모는 글만.
 * 카테고리 글이 21종 밖이면 assert.
 * 카테고리가 바뀌면 규칙 고르기도 처음으로 — `description_rule_id` null · `rules` [] · `reading` null(뷰가 규칙 목록을 다시 받는다 · 2026-10-26 fill).
 * 찾기 칸이 바뀌면 못 찾은 글 안내를 지운다 — `missed_query` null.
 */
export function formWithField(form: ProductFormData, field: EProductFormField, value: string): ProductFormData {
  const next = withFieldFollowUps(withFieldValue(form, field, value), field);
  if (!LOOKUP_KEY_FIELDS.includes(field)) return next;
  const bCatalogVendor = next.vendor !== '' && next.vendor === linkedVendorOrNull(form);   // 카탈로그가 넣은 제조사 — 연결이 풀리면 같이 비운다. 사람이 친 것은 둔다
  return { ...next, vendor: bCatalogVendor ? '' : next.vendor, picked: null, lookup: null, error: '' };
}

/**
 * 찾기 결과를 받은 폼.
 * 약속: 카탈로그에 있으면(`catalog` 있음) 카테고리를 **카탈로그 것으로 맞춘다**(카탈로그가 이김 — 21종 밖이면 assert) · 제조사도 카탈로그 것(카탈로그가 비었으면 친 것 그대로).
 * 없으면 카테고리 그대로. 이 뒤 `isCatalogLinked` 가 참이면 카테고리 셀렉트가 잠긴다(현민 2026-10-22 "기존 카탈로그를 선택시엔 수정이 안 되게").
 */
export function formWithLookup(form: ProductFormData, lookup: FormLookup): ProductFormData {
  if (lookup.catalog === null) return { ...form, lookup };
  const category = toSpecCategory(lookup.catalog.spec_category);
  assert(category !== null, `카탈로그 행의 카테고리는 21종 중 하나 — 서버가 표 이름으로 정했다: ${String(lookup.catalog.spec_category)}`);
  const vendor = lookup.catalog.vendor === null ? form.vendor : lookup.catalog.vendor;
  const vendorOther = lookup.catalog.vendor === null ? form.vendor_other : false;   // 카탈로그 제조사가 오면 기타 글 칸을 닫는다
  return { ...form, lookup, spec_category: category, vendor, vendor_other: vendorOther };
}

/**
 * 제조사 셀렉트를 고른 폼.
 * 약속: "기타" 면 vendor '' · vendor_other true(글 칸이 열려 사람이 친다). 그 밖(빈 값 · 후보)은 그 글 · vendor_other false.
 */
export function formWithVendorChoice(form: ProductFormData, value: string): ProductFormData {
  const bOther = value === VENDOR_OTHER;
  return { ...form, vendor: bOther ? '' : value, vendor_other: bOther };
}

/** 카탈로그에 있는 번호인가 — 담아 왔거나(`picked`) 찾기가 카탈로그 행을 줬다. 참이면 카테고리 셀렉트가 잠긴다(파트넘버를 바꾸면 풀린다 — formWithField 가 둘을 비운다) */
export function isCatalogLinked(form: ProductFormData): boolean {
  return form.picked !== null || (form.lookup !== null && form.lookup.catalog !== null);
}

/** 섀시 폼인가 — 카테고리가 CHASSIS. 찾기(서버 · 파트) · 저장(서버 · 파트) · 제조사 칸이 이 하나로 갈린다 */
export function isChassisForm(form: ProductFormData): boolean {
  return form.spec_category === CHASSIS_CATEGORY;
}

/** 못 찾은 글을 그 칸에 — formWithField 와 같은 길(파트넘버면 찾기 열쇠가 바뀐다), missed_query null */
export function formWithFoundText(form: ProductFormData, field: EFindFillField): ProductFormData {
  assert(form.missed_query !== null, `못 찾은 글이 없는데 [${field}] 버튼이 눌렸다 — missedNoteOf 가 그린 것과 어긋났다`);
  return { ...formWithField(form, field, form.missed_query), missed_query: null };
}

/** 규칙 읽기 상자가 보이나 — 카테고리 있음(섀시 포함 — 2026-10-29 ⑥) · 카탈로그에 안 이어짐 · 디스크립션이 비지 않음 */
export function isReadingShown(form: ProductFormData): boolean {
  return form.spec_category !== '' && !isCatalogLinked(form) && form.description.trim() !== '';
}

/**
 * 저장을 막는 이유 — 없으면 null([저장] 열림).
 * 약속: 순서대로 첫 이유 하나 — 저장 중 → 카테고리 안 고름 → 파트넘버 빔 → 디스크립션 빔 → 규칙으로 읽는 중 → 필수 자리 빔 → 찾기 결과에 같은 제품 있음("이미 등록된 제품입니다 — <번호>").
 * 필수 빔은 서버도 400 으로 막는다(2026-10-26 "필수는 입력 검사") — 여기는 먼저 알려 주는 자리.
 * 찾기를 아직 못 받았으면(`lookup` null · 파트넘버 있음) 막지 않는다 — 서버가 409 로 한 번 더 막는다.
 */
export function saveBlockOrNull(form: ProductFormData): string | null {
  if (form.saving) return '저장 중입니다';
  if (form.spec_category === '') return '카테고리를 고르세요';
  if (form.part_number.trim() === '') return '파트넘버를 적으세요';
  if (form.description.trim() === '') return '디스크립션을 적으세요';
  if (isReadingShown(form) && form.reading === null) return '디스크립션을 읽는 중입니다';
  if (isReadingShown(form) && missingRequiredRolesOf(form).length > 0) return '필수 자리가 비었습니다';
  const product = form.lookup === null ? null : form.lookup.product;
  if (product !== null) return `이미 등록된 제품입니다 — ${product.hclt_number}`;
  return null;   // 열린 요청은 저장을 막지 않는다 — 서버가 그 요청에 잇는다(2026-11-04)
}

/**
 * 파트 저장 입력(`POST /products`). 약속: `isChassisForm` 이 거짓일 때만(아니면 assert). 칸 글은 trim · 검색어 · 메모 빈 글은 그대로 ''(서버가 null 로 읽는다). `specs` null.
 */
/** 자리 하나의 채운 글 — 친 그대로 둔다(치는 중 공백 보존). 그 역할 줄을 바꾸고, 다 지우면 줄째 뺀다 */
export function formWithSlotFill(form: ProductFormData, role: EDescTokenRole, text: string): ProductFormData {
  return { ...form, slot_fills: withSlotFill(form.slot_fills, role, text) };
}

/** 카테고리를 골라 달라고 할 때 — 카테고리가 비었는데 파트넘버 · 디스크립션 중 하나라도 글이 있음(2026-10-26 — 못 찾은 글을 칸에 부은 직후가 이 상태) */
export function isCategoryNeeded(form: ProductFormData): boolean {
  const bTyped = form.part_number.trim() !== '' || form.description.trim() !== '';
  return form.spec_category === '' && bTyped;
}

/** 필수인데 빈 자리 — 읽힌 말도 채운 글도 없음. 읽기 결과가 없거나 규칙이 없으면 [] */
export function missingRequiredRolesOf(form: ProductFormData): EDescTokenRole[] {
  if (form.reading === null) return [];
  return missingSlotRolesOf(form.reading, form.slot_fills);
}

/** 보낼 채운 자리 — trim 하고 빈 글은 뺀다. 읽기 요청 · 저장 둘 다 */
export function slotFillsOf(form: ProductFormData): SlotFill[] {
  return trimmedFillsOf(form.slot_fills);
}

export function partInputOf(form: ProductFormData): PartProductInput {
  assert(!isChassisForm(form) && form.spec_category !== '', '파트 입력은 카테고리가 CHASSIS 가 아닌 21종 중 하나일 때만 — saveBlockOrNull 이 빈 카테고리를 걸렀다');
  return {
    part_number: form.part_number.trim(),
    vendor: form.vendor.trim(),
    spec_category: form.spec_category,
    description: form.description.trim(),
    specs: null,
    notes: form.notes.trim(),
    search_text: form.search_text.trim(),
    description_rule_id: form.description_rule_id,
    description_fills: slotFillsOf(form),
  };
}

/**
 * 섀시(서버) 저장 입력(`POST /products/server`). 약속: `isChassisForm` 이 참일 때만(아니면 assert). `chassis_sku` = 파트넘버 ·
 * `chassis_model_id` = `picked` 가 있으면 그 값, 수기면 null(서버가 SKU 로 찾고 없으면 섀시 요청) · `chassis_text` null · `items` [] · `safety_stock` null.
 */
export function serverInputOf(form: ProductFormData): ServerProductInput {
  assert(isChassisForm(form), '서버 입력은 카테고리가 CHASSIS 일 때만 — 뷰의 저장이 isChassisForm 으로 갈랐다');
  return {
    type: 'server',
    chassis_sku: form.part_number.trim(),
    chassis_model_id: form.picked === null ? null : form.picked.chassis_model_id,
    chassis_text: null,
    description: textOrNull(form.description),
    description_rule_id: form.description_rule_id,
    description_fills: slotFillsOf(form),
    items: [],
    notes: textOrNull(form.notes),
    search_text: textOrNull(form.search_text),
    safety_stock: null,                  // 폼에 없다(재고 쪽 — 2026-10-19)
  };
}

// ─── 안쪽 ───
/** 칸 하나의 값만 바꾼 폼 — 셀렉트 글은 열거로 좁힌다(아니면 assert · 셀렉트가 그 값만 그린다) */
/** 칸 하나가 바뀐 뒤 같이 비울 것 — 카테고리면 규칙 고르기를 처음으로 · 규칙이면 채운 자리를 비운다 · 찾기 칸이면 못 찾은 글 안내를 지운다 */
function withFieldFollowUps(form: ProductFormData, field: EProductFormField): ProductFormData {
  switch (field) {
    case 'spec_category': return { ...form, description_rule_id: null, rules: [], reading: null, slot_fills: [] };
    case 'description_rule_id': return { ...form, slot_fills: [] };   // 새 규칙에 없는 역할이 꼬리로 붙지 않게
    case 'catalog_query': return { ...form, missed_query: null };
    case 'part_number':
    case 'vendor':
    case 'description':
    case 'search_text':
    case 'notes':
      return form;
    default: {
      const never: never = field;
      throw new Error(`unhandled form field: ${String(never)}`);
    }
  }
}

function withFieldValue(form: ProductFormData, field: EProductFormField, value: string): ProductFormData {
  switch (field) {
    case 'catalog_query': return { ...form, catalog_query: value };
    case 'spec_category': return { ...form, spec_category: categoryOf(value) };
    case 'part_number': return { ...form, part_number: value };
    case 'vendor': return { ...form, vendor: value };
    case 'description': return { ...form, description: value };
    case 'description_rule_id': return { ...form, description_rule_id: value === '' ? null : value };
    case 'search_text': return { ...form, search_text: value };
    case 'notes': return { ...form, notes: value };
    default: {
      const never: never = field;
      throw new Error(`unhandled form field: ${String(never)}`);
    }
  }
}

/** 이어진 카탈로그 행의 제조사 — 담아 온 행 · 찾기 결과 순. 안 이어졌거나 비었으면 null */
function linkedVendorOrNull(form: ProductFormData): string | null {
  if (form.picked !== null) return form.picked.vendor;
  return form.lookup === null || form.lookup.catalog === null ? null : form.lookup.catalog.vendor;
}

function categoryOf(value: string): SpecCategory | '' {
  if (value === '') return '';
  const category = toSpecCategory(value);
  assert(category !== null, `카테고리 셀렉트의 값은 SPEC_CATEGORIES 21종 중 하나 — 뷰가 그 목록으로 그렸다: ${value}`);
  return category;
}

function textOrNull(text: string): string | null {
  const trimmed = text.trim();
  return trimmed === '' ? null : trimmed;
}
